/**
 * Lucid Charts OAuth 2.0 User Token Authentication Service
 * 
 * Handles OAuth 2.0 Authorization Code flow for Lucid Charts API access
 * using User Tokens to perform actions on behalf of authenticated users.
 */

class LucidAuthService {
  constructor() {
    // OAuth 2.0 endpoints and configuration
    this.CLIENT_ID = process.env.REACT_APP_LUCID_CLIENT_ID || '';
    this.CLIENT_SECRET = process.env.REACT_APP_LUCID_CLIENT_SECRET || '';
    this.REDIRECT_URI = process.env.REACT_APP_LUCID_REDIRECT_URI || `${window.location.origin}/auth/lucid/callback`;
    
    
    // Lucid OAuth 2.0 endpoints
    this.AUTHORIZATION_URL = 'https://lucid.app/oauth2/authorize';
    this.TOKEN_URL = 'https://api.lucid.co/oauth2/token';
    this.API_BASE_URL = 'https://api.lucid.co';
    
    // Required scopes for document creation and folder management
    this.SCOPES = [
      'lucidchart.document.content', // Create, view, edit, and delete account documents
      'user.profile', // View basic user information
      'folder', // Create and manage folders
      'offline_access' // Required for refresh token functionality
    ].join(' ');
    
    // Token storage keys
    this.ACCESS_TOKEN_KEY = 'lucid_access_token';
    this.REFRESH_TOKEN_KEY = 'lucid_refresh_token';
    this.TOKEN_EXPIRY_KEY = 'lucid_token_expiry';
    this.USER_INFO_KEY = 'lucid_user_info';
    
    // Bind methods
    this.initiateAuth = this.initiateAuth.bind(this);
    this.handleAuthCallback = this.handleAuthCallback.bind(this);
    this.getAccessToken = this.getAccessToken.bind(this);
    this.refreshToken = this.refreshToken.bind(this);
    this.isAuthenticated = this.isAuthenticated.bind(this);
    this.logout = this.logout.bind(this);
  }

  /**
   * Check if user is currently authenticated with valid token
   */
  isAuthenticated() {
    const accessToken = this.getStoredAccessToken();
    const expiry = this.getStoredTokenExpiry();
    
    if (!accessToken) {
      return false;
    }
    
    // Check if token is expired (with 5 minute buffer)
    if (expiry && new Date(expiry).getTime() - 300000 < Date.now()) {
      return false;
    }
    
    return true;
  }

  /**
   * Get stored user information
   */
  getUserInfo() {
    try {
      const userInfo = localStorage.getItem(this.USER_INFO_KEY);
      return userInfo ? JSON.parse(userInfo) : null;
    } catch (error) {
      console.error('Error retrieving user info:', error);
      return null;
    }
  }

  /**
   * Initiate OAuth 2.0 authorization flow
   */
  initiateAuth() {
    if (!this.CLIENT_ID) {
      throw new Error('Lucid Client ID not configured. Please set REACT_APP_LUCID_CLIENT_ID environment variable.');
    }

    // Generate state parameter for CSRF protection
    const state = this.generateRandomString(32);
    sessionStorage.setItem('lucid_oauth_state', state);

    // Build authorization URL
    const authParams = new URLSearchParams({
      client_id: this.CLIENT_ID,
      redirect_uri: this.REDIRECT_URI,
      response_type: 'code',
      scope: this.SCOPES,
      state: state
    });

    const authUrl = `${this.AUTHORIZATION_URL}?${authParams.toString()}`;
    
    // Open authorization in popup window
    const popup = window.open(
      authUrl,
      'lucid_auth',
      'width=600,height=700,scrollbars=yes,resizable=yes'
    );

    return new Promise((resolve, reject) => {
      // Check if popup was blocked
      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        reject(new Error('Popup blocked. Please allow popups for this site to authenticate with Lucid.'));
        return;
      }

      // Listen for messages from popup
      const messageHandler = async (event) => {
        if (event.origin !== window.location.origin) {
          return; // Ignore messages from other origins
        }

        if (event.data && event.data.type === 'LUCID_AUTH_SUCCESS') {
          try {
            cleanup();
            // Verify state parameter (main window has access to stored state)
            const storedState = sessionStorage.getItem('lucid_oauth_state');
            const receivedState = event.data.state;
            
            if (!receivedState || receivedState !== storedState) {
              throw new Error('Invalid state parameter. Possible CSRF attack.');
            }
            
            // Clean up state
            sessionStorage.removeItem('lucid_oauth_state');
            
            // Exchange code for token
            const tokenData = await this.exchangeCodeForToken(event.data.code);
            
            // Store tokens
            this.storeTokens(tokenData);
            
            // Get and store user information
            const userInfo = await this.fetchUserInfo();
            this.storeUserInfo(userInfo);
            
            const result = {
              success: true,
              accessToken: tokenData.access_token,
              userInfo: userInfo
            };
            
            resolve(result);
            
          } catch (error) {
            reject(error);
          }
        } else if (event.data && event.data.type === 'LUCID_AUTH_ERROR') {
          cleanup();
          reject(new Error(event.data.error || 'Authentication failed'));
        }
      };

      window.addEventListener('message', messageHandler);

      // Poll for popup completion (fallback)
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          cleanup();
          // Check if authentication was successful
          if (this.isAuthenticated()) {
            resolve({
              success: true,
              userInfo: this.getUserInfo()
            });
          } else {
            reject(new Error('Authentication cancelled or failed.'));
          }
        }
      }, 1000);

      // Cleanup function
      const cleanup = () => {
        clearInterval(checkClosed);
        window.removeEventListener('message', messageHandler);
        if (!popup.closed) {
          popup.close();
        }
      };

      // Set timeout for authentication
      setTimeout(() => {
        cleanup();
        reject(new Error('Authentication timeout. Please try again.'));
      }, 300000); // 5 minute timeout
    });
  }

  /**
   * Handle OAuth 2.0 callback (should be called from callback page)
   */
  async handleAuthCallback(url) {
    try {
      const urlParams = new URLSearchParams(new URL(url).search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');

      // Check for OAuth errors
      if (error) {
        throw new Error(`OAuth error: ${error} - ${urlParams.get('error_description') || 'Unknown error'}`);
      }

      // Verify state parameter
      const storedState = sessionStorage.getItem('lucid_oauth_state');
      if (!state || state !== storedState) {
        throw new Error('Invalid state parameter. Possible CSRF attack.');
      }

      // Clean up state
      sessionStorage.removeItem('lucid_oauth_state');

      if (!code) {
        throw new Error('Authorization code not received.');
      }

      // Exchange code for access token
      const tokenData = await this.exchangeCodeForToken(code);
      
      // Store tokens
      this.storeTokens(tokenData);
      
      // Get and store user information
      const userInfo = await this.fetchUserInfo();
      this.storeUserInfo(userInfo);

      return {
        success: true,
        accessToken: tokenData.access_token,
        userInfo: userInfo
      };

    } catch (error) {
      console.error('Auth callback error:', error);
      throw error;
    }
  }

  /**
   * Exchange authorization code for access token
   */
  async exchangeCodeForToken(code) {
    const tokenParams = new URLSearchParams({
      grant_type: 'authorization_code',
      code: code,
      redirect_uri: this.REDIRECT_URI,
      client_id: this.CLIENT_ID,
      client_secret: this.CLIENT_SECRET
    });

    const response = await fetch(this.TOKEN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
        'Lucid-Api-Version': '1'
      },
      body: tokenParams.toString()
    });

    if (!response.ok) {
      // Try to get detailed error information
      let errorData = {};
      let errorText = '';
      
      try {
        // Clone response to read it multiple ways
        const responseClone = response.clone();
        errorText = await responseClone.text();
        
        if (errorText) {
          try {
            errorData = JSON.parse(errorText);
          } catch (e) {
            // Error response is not JSON
          }
        }
      } catch (e) {
        // Could not read error response
      }
      
      const errorMessage = errorData.error_description || errorData.error || errorText || response.statusText || 'Unknown error';
      throw new Error(`Token exchange failed: ${errorMessage}`);
    }

    const tokenData = await response.json();
    
    if (!tokenData.access_token) {
      throw new Error('Access token not received from Lucid.');
    }

    return tokenData;
  }


  /**
   * Refresh access token using refresh token
   */
  async refreshToken() {
    const refreshToken = this.getStoredRefreshToken();
    
    if (!refreshToken) {
      throw new Error('No refresh token available.');
    }

    const tokenParams = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: this.CLIENT_ID,
      client_secret: this.CLIENT_SECRET,
      refresh_token: refreshToken
    });

    const response = await fetch(this.TOKEN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
        'Lucid-Api-Version': '1'
      },
      body: tokenParams.toString()
    });

    if (!response.ok) {
      // If refresh fails, user needs to re-authenticate
      this.logout();
      throw new Error('Token refresh failed. Please re-authenticate.');
    }

    const tokenData = await response.json();
    this.storeTokens(tokenData);
    
    return tokenData;
  }

  /**
   * Get valid access token (refresh if needed)
   */
  async getAccessToken() {
    if (!this.isAuthenticated()) {
      // Try to refresh token
      if (this.getStoredRefreshToken()) {
        try {
          await this.refreshToken();
        } catch (error) {
          throw new Error('Authentication expired. Please re-authenticate with Lucid.');
        }
      } else {
        throw new Error('Not authenticated with Lucid. Please authenticate first.');
      }
    }

    return this.getStoredAccessToken();
  }

  /**
   * Fetch user information from Lucid API
   */
  async fetchUserInfo() {
    const accessToken = this.getStoredAccessToken();
    
    if (!accessToken) {
      throw new Error('No access token available.');
    }

    const response = await fetch(`${this.API_BASE_URL}/users/me/profile`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/json',
        'Lucid-Api-Version': '1'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user info: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Logout and clear all stored tokens
   */
  logout() {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
    localStorage.removeItem(this.USER_INFO_KEY);
    sessionStorage.removeItem('lucid_oauth_state');
  }

  // Private helper methods

  /**
   * Store OAuth tokens securely
   */
  storeTokens(tokenData) {
    try {
      localStorage.setItem(this.ACCESS_TOKEN_KEY, tokenData.access_token);
      
      if (tokenData.refresh_token) {
        localStorage.setItem(this.REFRESH_TOKEN_KEY, tokenData.refresh_token);
      }
      
      if (tokenData.expires_in) {
        const expiryTime = new Date(Date.now() + (tokenData.expires_in * 1000));
        localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiryTime.toISOString());
      }
    } catch (error) {
      console.error('Error storing tokens:', error);
      throw new Error('Failed to store authentication tokens.');
    }
  }

  /**
   * Store user information
   */
  storeUserInfo(userInfo) {
    try {
      localStorage.setItem(this.USER_INFO_KEY, JSON.stringify(userInfo));
    } catch (error) {
      console.error('Error storing user info:', error);
    }
  }

  /**
   * Get stored access token
   */
  getStoredAccessToken() {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  /**
   * Get stored refresh token
   */
  getStoredRefreshToken() {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Get stored token expiry
   */
  getStoredTokenExpiry() {
    return localStorage.getItem(this.TOKEN_EXPIRY_KEY);
  }

  /**
   * Generate cryptographically secure random string (URL-safe)
   */
  generateRandomString(length) {
    // Use only URL-safe characters to avoid encoding issues
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
    let result = '';
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    
    for (let i = 0; i < length; i++) {
      result += chars[array[i] % chars.length];
    }
    
    return result;
  }

  /**
   * Create authenticated API client
   */
  async createApiClient() {
    const accessToken = await this.getAccessToken();
    
    return {
      get: async (endpoint) => {
        const response = await fetch(`${this.API_BASE_URL}${endpoint}`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Accept': 'application/json',
            'Lucid-Api-Version': '1'
          }
        });
        
        if (!response.ok) {
          throw new Error(`API request failed: ${response.statusText}`);
        }
        
        return await response.json();
      },
      
      post: async (endpoint, data) => {
        const headers = {
          'Authorization': `Bearer ${accessToken}`,
          'Lucid-Api-Version': '1'
        };
        
        let body;
        if (data instanceof FormData) {
          body = data;
        } else {
          headers['Content-Type'] = 'application/json';
          body = JSON.stringify(data);
        }
        

        const response = await fetch(`${this.API_BASE_URL}${endpoint}`, {
          method: 'POST',
          headers,
          body
        });
        
        if (!response.ok) {
          // Try to get detailed error information
          let errorDetails = '';
          try {
            const errorText = await response.text();
            errorDetails = errorText;
          } catch (e) {
            // Could not read API error response
          }
          
          throw new Error(`API request failed: ${response.statusText}${errorDetails ? ' - ' + errorDetails : ''}`);
        }
        
        return await response.json();
      },

      // Folder management endpoints
      createFolder: async (folderData) => {
        const headers = {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Lucid-Api-Version': '1'
        };

        const response = await fetch(`${this.API_BASE_URL}/folders`, {
          method: 'POST',
          headers,
          body: JSON.stringify(folderData)
        });

        if (!response.ok) {
          let errorDetails = '';
          try {
            const errorText = await response.text();
            errorDetails = errorText;
          } catch (e) {
            // Could not read folder creation error response
          }

          throw new Error(`Folder creation failed: ${response.statusText}${errorDetails ? ' - ' + errorDetails : ''}`);
        }

        return await response.json();
      },

      getFolders: async (parentId = null) => {
        const queryParams = parentId ? `?parent=${parentId}` : '';
        const response = await fetch(`${this.API_BASE_URL}/folders${queryParams}`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Accept': 'application/json',
            'Lucid-Api-Version': '1'
          }
        });

        if (!response.ok) {
          throw new Error(`Get folders failed: ${response.statusText}`);
        }

        return await response.json();
      },

      getFolder: async (folderId) => {
        const response = await fetch(`${this.API_BASE_URL}/folders/${folderId}`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Accept': 'application/json',
            'Lucid-Api-Version': '1'
          }
        });

        if (!response.ok) {
          throw new Error(`Get folder failed: ${response.statusText}`);
        }

        return await response.json();
      }
    };
  }
}

// Export singleton instance
export const lucidAuthService = new LucidAuthService();
export default lucidAuthService;